const clamp = (value, min = 0, max = 255) =>
  Math.min(max, Math.max(min, Math.round(value)));

const normalizeHex = (hex) => {
  const value = hex.replace("#", "").trim();
  if (value.length === 3) {
    return value
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }
  return value;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) => clamp(channel).toString(16).padStart(2, "0"))
    .join("")}`;

const mixColors = (sourceHex, targetHex, amount) => {
  const source = hexToRgb(sourceHex);
  const target = hexToRgb(targetHex);
  const mix = (start, end) => start + (end - start) * amount;

  return rgbToHex({
    r: mix(source.r, target.r),
    g: mix(source.g, target.g),
    b: mix(source.b, target.b),
  });
};

const rgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const ACCENT_CONFIG = {
  primary: "#c8421a",
};

const primary = ACCENT_CONFIG.primary;
const primaryDarkMode = mixColors(primary, "#ffffff", 0.2);

export const accentTheme = {
  primary,
  primaryRgb: `${hexToRgb(primary).r}, ${hexToRgb(primary).g}, ${hexToRgb(primary).b}`,
  primaryDarkMode,
  primaryDarkModeRgb: `${hexToRgb(primaryDarkMode).r}, ${hexToRgb(primaryDarkMode).g}, ${hexToRgb(primaryDarkMode).b}`,
  contrastText: "#ffffff",
  hover: mixColors(primary, "#000000", 0.12),
  deep: mixColors(primary, "#000000", 0.3),
  gradientEnd: mixColors(primary, "#ffffff", 0.18),
  gradientHoverEnd: mixColors(primary, "#ffffff", 0.1),
  gradientSoftStart: mixColors(primary, "#ffffff", 0.88),
  gradientSoftEnd: mixColors(primary, "#ffffff", 0.72),
  lightSurface: mixColors(primary, "#ffffff", 0.82),
  lightSurfaceStrong: mixColors(primary, "#ffffff", 0.9),
  softGlow: rgba(primary, 0.18),
  glow: rgba(primary, 0.28),
  ring: rgba(primary, 0.12),
  border: rgba(primary, 0.26),
  mutedBorder: rgba(primary, 0.16),
  darkModeSoft: rgba(primaryDarkMode, 0.18),
  darkModeGlow: rgba(primaryDarkMode, 0.18),
  darkModeRing: rgba(primaryDarkMode, 0.16),
};

export const initializeAccentTheme = () => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.style.setProperty("--accent-primary", accentTheme.primary);
  root.style.setProperty("--accent-primary-rgb", accentTheme.primaryRgb);
  root.style.setProperty("--accent-primary-dark", accentTheme.primaryDarkMode);
  root.style.setProperty(
    "--accent-primary-dark-rgb",
    accentTheme.primaryDarkModeRgb,
  );
  root.style.setProperty("--accent-hover", accentTheme.hover);
  root.style.setProperty("--accent-deep", accentTheme.deep);
  root.style.setProperty("--accent-gradient-end", accentTheme.gradientEnd);
  root.style.setProperty(
    "--accent-gradient-soft-start",
    accentTheme.gradientSoftStart,
  );
  root.style.setProperty(
    "--accent-gradient-soft-end",
    accentTheme.gradientSoftEnd,
  );
  root.style.setProperty(
    "--accent-gradient-hover-end",
    accentTheme.gradientHoverEnd,
  );
  root.style.setProperty("--accent-light-surface", accentTheme.lightSurface);
  root.style.setProperty(
    "--accent-light-surface-strong",
    accentTheme.lightSurfaceStrong,
  );
  root.style.setProperty("--accent-soft-glow", accentTheme.softGlow);
  root.style.setProperty("--accent-glow", accentTheme.glow);
  root.style.setProperty("--accent-ring", accentTheme.ring);
  root.style.setProperty("--accent-border", accentTheme.border);
  root.style.setProperty("--accent-muted-border", accentTheme.mutedBorder);
  root.style.setProperty("--accent-dark-soft", accentTheme.darkModeSoft);
  root.style.setProperty("--accent-dark-glow", accentTheme.darkModeGlow);
  root.style.setProperty("--accent-dark-ring", accentTheme.darkModeRing);
  root.style.setProperty("--accent-contrast-text", accentTheme.contrastText);
};
